'use client';

/**
 * ProjectDetails - Project entity detail view
 *
 * @deprecated Use EntityDetailsLoader with DetailsPanel instead.
 * This component is kept for fallback during migration.
 * Remove after schema-driven rendering is verified.
 *
 * Displays project information when a project node is selected.
 * Fetches its own data via useProjectExplore hook.
 *
 * @component_role Project Detail View
 * @business_rule Shows project status, manager, timeline, customer,
 *   summary stats (RFQs/POs/Vendors), and recent RFQs/POs list.
 * @user_outcome Project overview at a glance - see progress, team,
 *   timeline, and active procurement documents.
 * @integration_point klyde_edge /neo/graph/explore/project
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { cn } from '@/lib/utils/cn';
import {
  ChevronRight,
  User,
  FileText,
  Package,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { useProjectExplore } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';

interface ProjectDetailsProps {
  nodeId: string;
}

export function ProjectDetails({ nodeId }: ProjectDetailsProps) {
  const projectExploreStrategy = useCapabilityStore((s) => s.strategyName('project_intelligence', 'explore'));
  const { data, isLoading } = useProjectExplore(projectExploreStrategy!, {
    project_id: nodeId,
    include_relationships: ['rfqs', 'purchase_orders'],
    max_items: 50
  });
  const project = data?.project;

  const ODOO_HOST = process.env.NEXT_PUBLIC_ODOO_URL || 'http://klyde.vivita-ai.com';

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading project...
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-sm text-muted-foreground">
        Project details not available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status & Manager Row */}
      <div className="flex items-start gap-4">
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-1">STATUS</h4>
          <span className={cn(
            "inline-block px-2 py-0.5 rounded text-xs",
            project.status === 'in_progress' && "bg-info/20 text-info",
            project.status === 'completed' && "bg-success/20 text-success",
            project.status === 'on_hold' && "bg-warning/20 text-warning",
            !project.status && "bg-muted text-muted-foreground"
          )}>
            {project.status?.replace('_', ' ') || 'unknown'}
          </span>
        </div>
        {project.manager_name && (
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground mb-1">MANAGER</h4>
            <span className="text-sm">{project.manager_name}</span>
          </div>
        )}
      </div>

      {/* Timeline */}
      {(project.start_date || project.target_date) && (
        <div className="flex items-center gap-4 p-2 rounded bg-card border border-border">
          {project.start_date && (
            <div>
              <div className="text-xs text-muted-foreground">Start</div>
              <div className="text-sm font-medium">{project.start_date}</div>
            </div>
          )}
          {project.start_date && project.target_date && (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          {project.target_date && (
            <div>
              <div className="text-xs text-muted-foreground">Target</div>
              <div className="text-sm font-medium">{project.target_date}</div>
            </div>
          )}
        </div>
      )}

      {/* Customer */}
      {project.customer && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-1">CUSTOMER</h4>
          <div className="flex items-center gap-2 p-2 rounded bg-card border border-border">
            <User className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">{project.customer.name}</div>
              {project.customer.type && (
                <div className="text-xs text-muted-foreground capitalize">{project.customer.type}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="p-2 rounded bg-card border border-border text-center">
          <div className="text-lg font-semibold">{data?.metadata?.rfq_count}</div>
          <div className="text-xs text-muted-foreground">RFQs</div>
        </div>
        <div className="p-2 rounded bg-card border border-border text-center">
          <div className="text-lg font-semibold">{data?.metadata?.po_count}</div>
          <div className="text-xs text-muted-foreground">POs</div>
        </div>
        <div className="p-2 rounded bg-card border border-border text-center">
          <div className="text-lg font-semibold">{data?.metadata?.total_vendors}</div>
          <div className="text-xs text-muted-foreground">Vendors</div>
        </div>
      </div>

      {/* RFQs */}
      {project.rfqs.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">
            RFQS ({project.rfqs.length})
          </h4>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {project.rfqs.map((rfq) => (
              <div key={rfq.semantic_id} className="flex items-center justify-between p-2 rounded bg-card border border-border">
                <div className="flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium">{rfq.name || rfq.semantic_id}</span>
                </div>
                <span className={cn(
                  "text-xs px-1.5 py-0.5 rounded",
                  rfq.state === 'sent' && "bg-info/20 text-info",
                  rfq.state === 'draft' && "bg-muted text-muted-foreground",
                  rfq.state === 'done' && "bg-success/20 text-success"
                )}>
                  {rfq.state || 'draft'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* POs */}
      {project.purchase_orders.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">
            PURCHASE ORDERS ({project.purchase_orders.length})
          </h4>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {project.purchase_orders.map((po) => (
              <div key={po.semantic_id} className="flex items-center justify-between p-2 rounded bg-card border border-border">
                <div className="flex items-center gap-2">
                  <Package className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium">{po.name || po.semantic_id}</span>
                </div>
                <span className={cn(
                  "text-xs px-1.5 py-0.5 rounded",
                  po.state === 'purchase' && "bg-success/20 text-success",
                  po.state === 'draft' && "bg-muted text-muted-foreground",
                  po.state === 'sent' && "bg-info/20 text-info"
                )}>
                  {po.state || 'draft'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Open in Odoo */}
      {project.odoo_id && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">ACTIONS</h4>
          <a
            href={`${ODOO_HOST}/web#id=${project.odoo_id}&model=project.project&view_type=form`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 w-full p-2 rounded-lg hover:bg-accent transition-colors text-sm"
          >
            <ExternalLink className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">{project.name || 'Project'}</span>
          </a>
        </div>
      )}
    </div>
  );
}
